import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { RelayType } from '@diritto/common/content/constants';
import { ContentPublicRelayDto, ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { fetchActiveContentContestIds } from 'queries/contests/useGetContentContestsQuery';

type ContentContestState = {
  activeContentContestIds: Set<number>;
};

type ContentContestDispatch = {
  checkActiveContentContests: (contestId: number, contentContestIds: number[]) => Promise<void>;
  checkIsContentContestActive: (contentContestId?: number | null) => boolean;
};

const ActiveContentContestIdsStateContext = createContext<ContentContestState | null>(null);
const ActiveContentContestIdsDispatchContext = createContext<ContentContestDispatch | null>(null);

export const extractContentContestIds = (contentIds: number[], contents: Record<number, ContentRelayDto>) => {
  const contentContestIds = contentIds
    .map(id => {
      const content = contents[id];
      if (content?.relayType !== RelayType.Public) {
        return undefined;
      }
      return (content as ContentPublicRelayDto).contentContestId;
    })
    .filter((id): id is number => typeof id === 'number');
  return contentContestIds;
};

export const ActiveContentContestIdsProvider = ({
  children,
  activeContentContestIds: initialActiveContentContestIds,
}: {
  children: ReactNode;
  activeContentContestIds?: number[];
}) => {
  const queryClient = useQueryClient();

  const [activeContentContestIdSet, setActiveContentContestIdSet] = useState(
    () => new Set(initialActiveContentContestIds ?? []),
  );

  const checkedContentContestIdsRef = useRef(new Set<number>(initialActiveContentContestIds ?? []));

  const checkActiveContentContests = useCallback(
    async (contestId: number, contentContestIds: number[]) => {
      const uniqueContentContestIds = Array.from(new Set(contentContestIds));

      const newContentContestIds = uniqueContentContestIds.filter(id => !checkedContentContestIdsRef.current.has(id));

      if (newContentContestIds.length === 0) {
        return;
      }

      newContentContestIds.forEach(id => checkedContentContestIdsRef.current.add(id));

      try {
        const data = await fetchActiveContentContestIds(queryClient, contestId, newContentContestIds);

        if (data?.activeContentContestIds) {
          setActiveContentContestIdSet(prev => {
            const newSet = new Set(prev);
            data.activeContentContestIds.forEach(id => {
              newSet.add(id);
            });
            return newSet;
          });
        }
      } catch (error) {
        console.error('Failed to check active content contests', error);
      }
    },
    [queryClient],
  );

  const checkIsContentContestActive = useCallback(
    (contentContestId?: number | null) => {
      if (!contentContestId) {
        return false;
      }
      return activeContentContestIdSet.has(contentContestId);
    },
    [activeContentContestIdSet],
  );

  const state = useMemo(() => ({ activeContentContestIds: activeContentContestIdSet }), [activeContentContestIdSet]);

  const dispatch = useMemo(
    () => ({
      checkActiveContentContests,
      checkIsContentContestActive,
    }),
    [checkActiveContentContests, checkIsContentContestActive],
  );

  return (
    <ActiveContentContestIdsStateContext.Provider value={state}>
      <ActiveContentContestIdsDispatchContext.Provider value={dispatch}>
        {children}
      </ActiveContentContestIdsDispatchContext.Provider>
    </ActiveContentContestIdsStateContext.Provider>
  );
};

export const useActiveContentContestIdsState = () => {
  const context = useContext(ActiveContentContestIdsStateContext);
  if (!context) {
    throw new UsedOutsideProviderError('ActiveContentContestIdsProvider');
  }
  return context;
};

export const useActiveContentContestIdsDispatch = () => {
  const context = useContext(ActiveContentContestIdsDispatchContext);
  if (!context) {
    throw new UsedOutsideProviderError('ActiveContentContestIdsProvider');
  }
  return context;
};
